import { useSignal, useSignalEffect } from "@preact/signals";
import { useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { api, hasPermission } from "../store";
import { PageHead, Panel } from "../components";
import type { ApiResponse } from "../api";
import { t } from "../i18n";
import { isPublicIp } from "../../geoip/ip";
import {
  formatTeam, teamTone, formatKitName, formatVehicleName, formatProvider, formatSeenCount, clanTagPill, DASH,
} from "../player-intel-format";

/** Team-Rohwert → numerisches Team fürs faktions-korrekte Vehicle-Mapping. */
function teamNum(raw: string | null | undefined): 0 | 1 | 2 {
  const f = formatTeam(raw);
  return f === "PAC" ? 1 : f === "EU" ? 2 : 0;
}

interface PlayerSummary {
  pid: string;
  name: string;
  clanTag: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  lastServerId: string | null;
  lastServerName: string | null;
  seenCount: number;
  geoCountryCode?: string | null;
  geoCountryName?: string | null;
}

interface HistoryValue {
  value: string;
  firstSeenAt: number;
  lastSeenAt: number;
  seenCount: number;
}

interface ServerHistory {
  serverId: string;
  serverName: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  seenCount: number;
  lastTeam: string | null;
  lastSquad: string | null;
  lastKit: string | null;
  lastVehicle: string | null;
  lastRank: string | null;
}

interface IpHistory extends HistoryValue {
  geo?: {
    countryCode: string | null;
    countryName: string | null;
    region: string | null;
    city: string | null;
    asn: string | null;
    org: string | null;
    provider: string | null;
    lookedUpAt: number | null;
    error: string | null;
  } | null;
}

interface ChatLine {
  id: number;
  serverId: string;
  playerName: string;
  channel: string;
  team: string | null;
  message: string;
  sentAt: number;
}

interface PlayerDetail {
  summary: PlayerSummary;
  names: HistoryValue[];
  clanTags: HistoryValue[];
  servers: ServerHistory[];
  ips?: IpHistory[];
  chat?: ChatLine[];
  pidBan?: { name?: string; reason?: string; by?: string; ts?: string } | null;
  /** Serverseitiger GeoIP-Status (Issue #12): false = kein Provider konfiguriert. */
  geoEnabled?: boolean;
  /** GeoIP-Provider-Status: ok | disabled | unconfigured | db_missing. */
  geoStatus?: string;
}

function fmtTs(ts: number): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString();
}

function serverLabel(s: { serverId?: string | null; serverName?: string | null; lastServerId?: string | null; lastServerName?: string | null }): string {
  return s.serverName || s.lastServerName || s.serverId || s.lastServerId || "—";
}

function Empty({ children }: { children: ComponentChildren }) {
  return <div class="empty intel-empty">{children}</div>;
}

/** Name-/ClanTag-Historie als Listen-Cards (statt Tabellenwand). */
function HistoryList({ rows, label, current, kind }: { rows: HistoryValue[]; label: string; current: string | null; kind: "name" | "clan" }) {
  if (rows.length === 0) return <Empty>{t("playerIntel.noStoredPre")} {label} {t("playerIntel.noStoredPost")}</Empty>;
  return (
    <div class="pi-list">
      {rows.map((r) => {
        const cur = current != null && current !== "" && r.value === current;
        return (
          <div class={`pi-row ${cur ? "current" : ""}`} key={`${kind}-${r.value}`}>
            <div class="pi-row-main">
              <div class="pi-row-title">
                {kind === "clan" ? <span class="pi-pill mono tag">{r.value}</span> : <span class="pi-strong">{r.value}</span>}
                {cur && <span class="badge ok pi-current">{t("playerIntel.current")}</span>}
              </div>
              <div class="pi-row-meta">{t("playerIntel.firstSeen")} {fmtTs(r.firstSeenAt)} · {t("playerIntel.lastSeen")} {fmtTs(r.lastSeenAt)}</div>
            </div>
            <span class="pi-pill seen" title={t("playerIntel.seenCount")}>{formatSeenCount(r.seenCount)}×</span>
          </div>
        );
      })}
    </div>
  );
}

function PlayerDetailPanel({ player, canViewIp, canViewGeo, onRefreshGeo, geoLooking }: { player: PlayerDetail | null; canViewIp: boolean; canViewGeo: boolean; onRefreshGeo?: () => void; geoLooking?: boolean }) {
  if (!player) {
    return (
      <Panel title={t("view.playerDetail")}>
        <Empty>{t("playerIntel.selectPlayer")}</Empty>
      </Panel>
    );
  }
  const p = player.summary;
  // Klarer GeoIP-Status statt stiller Dashes (Issue #12 / MMDB-Slice).
  const geoUnavail = player.geoStatus && player.geoStatus !== "ok"
    ? (player.geoStatus === "db_missing" ? t("playerIntel.geoDbMissing") : t("playerIntel.geoNotConfigured"))
    : null;
  const geoSummary = canViewGeo ? (p.geoCountryName || p.geoCountryCode || null) : null;
  return (
    <Panel title={t("view.playerDetail")}>
      <div class="pi-profile">
        <div class="pi-header">
          <span class="pi-name">{p.name}</span>
          <span class="pi-pill mono">PID {p.pid}</span>
          {p.clanTag && <span class="badge accent">{p.clanTag}</span>}
          {player.pidBan && <span class="badge warn" title={player.pidBan.reason ?? ""}>PID BAN</span>}
        </div>
        <div class="pi-meta">
          <span>{t("playerIntel.lastSeen")} {fmtTs(p.lastSeenAt)}</span>
          <span>{serverLabel(p)}</span>
          {!geoUnavail && geoSummary && <span>{geoSummary}</span>}
        </div>
      </div>

      <div class="pi-summary-grid">
        <div class="pi-card"><span class="label">ClanTag</span><b>{p.clanTag ?? DASH}</b></div>
        <div class="pi-card"><span class="label">{t("playerIntel.lastServer")}</span><b>{serverLabel(p)}</b></div>
        <div class="pi-card"><span class="label">{t("playerIntel.lastSeen")}</span><b>{fmtTs(p.lastSeenAt)}</b></div>
        <div class="pi-card"><span class="label">{t("playerIntel.seenCount")}</span><b>{formatSeenCount(p.seenCount)}</b></div>
        {canViewGeo && (
          <div class="pi-card"><span class="label">Geo</span><b>{geoUnavail ? <span class="muted">{geoUnavail}</span> : (geoSummary ?? DASH)}</b></div>
        )}
      </div>

      <div class="pi-section">
        <div class="pi-section-head"><h3>{t("playerIntel.nameHistory")}</h3></div>
        <HistoryList rows={player.names} label="Name" current={p.name} kind="name" />
      </div>

      <div class="pi-section">
        <div class="pi-section-head"><h3>{t("playerIntel.clanTagHistory")}</h3></div>
        <HistoryList rows={player.clanTags} label="ClanTag" current={p.clanTag} kind="clan" />
      </div>

      <div class="pi-section">
        <div class="pi-section-head"><h3>{t("playerIntel.serverHistory")}</h3></div>
        {player.servers.length === 0 ? <Empty>{t("playerIntel.noServerHistory")}</Empty> : (
          <div class="pi-list">
            {player.servers.map((s) => {
              const team = formatTeam(s.lastTeam);
              return (
                <div class="pi-row" key={s.serverId}>
                  <div class="pi-row-main">
                    <div class="pi-row-title"><span class="pi-strong">{serverLabel(s)}</span></div>
                    <div class="pi-row-meta">
                      <span>{t("playerIntel.lastSeen")} {fmtTs(s.lastSeenAt)}</span>
                      {team !== DASH && <span class={`badge team ${teamTone(s.lastTeam)}`}>{team}</span>}
                      {s.lastSquad && s.lastSquad !== "0" && <span class="pi-pill">SQ {s.lastSquad}</span>}
                      <span>{t("playerIntel.kit")}: <b>{formatKitName(s.lastKit)}</b></span>
                      <span>{t("playerIntel.vehicle")}: <b>{formatVehicleName(s.lastVehicle, teamNum(s.lastTeam))}</b></span>
                    </div>
                  </div>
                  <span class="pi-pill seen" title={t("playerIntel.seenCount")}>{formatSeenCount(s.seenCount)}×</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div class="pi-section">
        <div class="pi-section-head">
          <h3>{t("playerIntel.ipHistory")}</h3>
          {canViewIp && canViewGeo && onRefreshGeo && (!player.geoStatus || player.geoStatus === "ok") && (
            <button class="btn sm" type="button" onClick={onRefreshGeo} disabled={geoLooking}>
              {geoLooking ? t("playerIntel.geoLooking") : `↻ ${t("playerIntel.refreshGeo")}`}
            </button>
          )}
        </div>
        {!canViewIp ? <Empty>{t("playerIntel.ipRequiresPerm")}</Empty> : player.ips?.length ? (
          <div class="pi-list">
            {player.ips.map((ip) => {
              const geoLine = [ip.geo?.countryName, ip.geo?.city].filter(Boolean).join(" · ");
              const asn = ip.geo?.asn;
              const prov = formatProvider(ip.geo?.provider || ip.geo?.org);
              const asnLine = [asn, prov !== DASH ? prov : null].filter(Boolean).join(" · ");
              return (
                <div class="pi-ip-card" key={ip.value}>
                  <span class="pi-pill mono ip">{ip.value}</span>
                  <div class="pi-ip-info">
                    <span class="pi-ip-when">{t("playerIntel.lastSeen")} {fmtTs(ip.lastSeenAt)}</span>
                    {!canViewGeo ? (
                      <span>{t("playerIntel.requiresViewGeo")}</span>
                    ) : geoUnavail ? (
                      <span>{geoUnavail}</span>
                    ) : ip.geo?.error ? (
                      <span class="pi-ip-err">{t("common.error")}: {ip.geo.error}</span>
                    ) : (
                      <>
                        {geoLine && <span class="pi-ip-geo">{geoLine}</span>}
                        {asnLine && <span class="pi-ip-provider">{asnLine}</span>}
                        {!geoLine && !asnLine && <span>{DASH}</span>}
                      </>
                    )}
                  </div>
                  <span class="pi-pill seen" title={t("playerIntel.seenCount")}>{formatSeenCount(ip.seenCount)}×</span>
                </div>
              );
            })}
          </div>
        ) : <Empty>{t("playerIntel.noIpHistory")}</Empty>}
      </div>

      <div class="pi-section">
        <div class="pi-section-head"><h3>{t("playerIntel.chatHistory")}</h3></div>
        {player.chat == null ? <Empty>{t("playerIntel.chatRequiresPerm")}</Empty> : player.chat.length === 0 ? <Empty>{t("playerIntel.noChatLines")}</Empty> : (
          <div class="pi-list">
            {player.chat.map((c) => (
              <div class="pi-chat-card" key={c.id}>
                <span class="badge">{c.channel || "CHAT"}</span>
                <span class="pi-chat-meta muted">{fmtTs(c.sentAt)} · {c.serverId}</span>
                <span class="pi-chat-message">{c.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

/**
 * Welche IPs einer Player-Intel-Detailansicht sollen automatisch per
 * `geoip_lookup` aufgelöst werden (Issue: GeoIP UX)?
 *
 * Regeln (kein Leak, kein Loop, keine unnötige Last):
 * - nur wenn der User IP UND Geo sehen darf (`action.view_ip` + `action.view_geo`),
 * - nur IP-Zeilen OHNE Geo-Cache (`geo == null`) — vorhandener Cache wird NICHT
 *   erneut aufgelöst,
 * - nur ÖFFENTLICHE IPs (private/reservierte werden nie gelookupt),
 * - nur IPs, die noch nicht versucht wurden (`attempted`) → kein Doppel-Lookup
 *   für dieselbe IP und keine Endlosschleife.
 * Rückgabe: deduplizierte Liste der nachzuschlagenden IPs.
 */
export function ipsNeedingGeoLookup(
  ips: ReadonlyArray<{ value: string; geo?: unknown }> | undefined,
  canViewIp: boolean,
  canViewGeo: boolean,
  attempted: ReadonlySet<string>,
): string[] {
  if (!canViewIp || !canViewGeo || !ips) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const ip of ips) {
    const v = (ip.value ?? "").trim();
    if (!v || seen.has(v) || attempted.has(v)) continue;
    if (ip.geo != null) continue;        // Cache vorhanden -> nicht erneut auflösen
    if (!isPublicIp(v)) continue;        // privat/reserviert -> nie auto-lookupen
    seen.add(v);
    out.push(v);
  }
  return out;
}

export function PlayerIntel() {
  const q = useSignal("");
  const loading = useSignal(false);
  const players = useSignal<PlayerSummary[]>([]);
  const selectedPid = useSignal<string>("");
  const detail = useSignal<PlayerDetail | null>(null);
  const error = useSignal("");

  const canViewIp = hasPermission("action.view_ip");
  const canViewGeo = hasPermission("action.view_geo");

  // Auto-GeoIP-Lookup: bereits versuchte IPs (gegen Loop/Doppel-Lookup) + ein
  // In-Flight-Guard (kein paralleler Lookup). Beides kein Re-render → useRef.
  const geoAttempted = useRef<Set<string>>(new Set());
  const geoLookupBusy = useRef(false);
  const geoLooking = useSignal(false);

  async function loadSearch() {
    loading.value = true;
    error.value = "";
    try {
      const r = await api.call<ApiResponse & { players: PlayerSummary[] }>("player_intel_search", { q: q.value, limit: 100 });
      if (r.ok) {
        players.value = r.players;
        if (!selectedPid.value && r.players[0]) void select(r.players[0].pid);
      } else {
        error.value = r.error ?? t("playerIntel.loadFailed");
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t("playerIntel.loadFailed");
    } finally {
      loading.value = false;
    }
  }

  async function select(pid: string) {
    selectedPid.value = pid;
    error.value = "";
    const r = await api.call<ApiResponse & { player: PlayerDetail; geoEnabled?: boolean; geoStatus?: string }>("player_intel_detail", { pid });
    if (r.ok) {
      const d: PlayerDetail = { ...r.player, geoEnabled: r.geoEnabled, geoStatus: r.geoStatus };
      detail.value = d;
      void maybeAutoLookupGeo(pid, d);
    } else error.value = r.error ?? t("playerIntel.detailLoadFailed");
  }

  /**
   * Löst fehlende GeoIP-Caches für die IPs des Players automatisch über die
   * bestehende, server-seitig gegatete Action `geoip_lookup` auf und lädt danach
   * das Detail neu. Permission-/Privat-IP-/Loop-Schutz steckt in
   * `ipsNeedingGeoLookup`; Fehler werden still defensiv behandelt (kein UI-Spam).
   */
  async function maybeAutoLookupGeo(pid: string, d: PlayerDetail) {
    if (geoLookupBusy.current) return;
    // Provider nicht nutzbar (nicht konfiguriert / DB fehlt / deaktiviert) → kein
    // sinnloser Lookup-Spam; die UI zeigt ohnehin den klaren Status.
    if (d.geoStatus && d.geoStatus !== "ok") return;
    const targets = ipsNeedingGeoLookup(d.ips, canViewIp, canViewGeo, geoAttempted.current);
    if (targets.length === 0) return;
    geoLookupBusy.current = true;
    geoLooking.value = true;
    try {
      // VOR dem Request markieren → kein Doppel-Lookup / keine Endlosschleife,
      // selbst wenn der Lookup nichts findet (Cache bleibt dann leer).
      for (const ip of targets) geoAttempted.current.add(ip);
      let any = false;
      for (const ip of targets) {
        try {
          const r = await api.call<ApiResponse>("geoip_lookup", { ip });
          if (r.ok) any = true;
        } catch {
          /* still defensiv: Einzelfehler nicht ins UI spammen, attempted blockt Retry */
        }
      }
      // Nur neu laden, wenn noch derselbe Player offen ist (User könnte gewechselt haben).
      if (any && selectedPid.value === pid) await select(pid);
    } finally {
      geoLookupBusy.current = false;
      geoLooking.value = false;
    }
  }

  /**
   * Manueller „GeoIP aktualisieren"-Button (sekundär): löst ALLE öffentlichen IPs
   * des Players neu auf (auch bereits gecachte) und lädt das Detail neu.
   */
  async function refreshGeo() {
    const d = detail.value;
    const pid = selectedPid.value;
    if (!d || !pid || !canViewIp || !canViewGeo || geoLookupBusy.current) return;
    if (d.geoStatus && d.geoStatus !== "ok") return;
    const ips = Array.from(new Set((d.ips ?? []).map((i) => (i.value ?? "").trim()).filter((v) => v && isPublicIp(v))));
    if (ips.length === 0) return;
    geoLookupBusy.current = true;
    geoLooking.value = true;
    try {
      for (const ip of ips) {
        geoAttempted.current.add(ip);
        try { await api.call<ApiResponse>("geoip_lookup", { ip }); } catch { /* defensiv */ }
      }
      if (selectedPid.value === pid) await select(pid);
    } finally {
      geoLookupBusy.current = false;
      geoLooking.value = false;
    }
  }

  useSignalEffect(() => { void loadSearch(); });

  return (
    <div class="view-enter player-intel-view">
      <PageHead
        title={t("view.playerIntel")}
        sub={t("playerIntel.sub")}
      >
        <button class="btn sm" onClick={() => void loadSearch()} disabled={loading.value}>↻ {t("common.refresh")}</button>
      </PageHead>

      <div class="intel-layout">
        <Panel title={t("view.playerSearch")}>
          <form class="intel-search" onSubmit={(e) => { e.preventDefault(); void loadSearch(); }}>
            <input
              class="input"
              value={q.value}
              onInput={(e) => (q.value = (e.target as HTMLInputElement).value)}
              placeholder={canViewIp ? t("playerIntel.searchPlaceholderIp") : t("playerIntel.searchPlaceholder")}
            />
            <button class="btn primary" disabled={loading.value}>{t("common.search")}</button>
          </form>
          <div class="intel-permission-row">
            <span class={`badge ${canViewIp ? "ok" : ""}`}>IP {canViewIp ? t("status.visible") : t("status.hidden")}</span>
            <span class={`badge ${canViewGeo ? "ok" : ""}`}>Geo {canViewGeo ? t("status.visible") : t("status.hidden")}</span>
          </div>
          {error.value && <div class="banner error">{error.value}</div>}
          <div class="intel-player-list">
            {players.value.length === 0 ? (
              <Empty>{loading.value ? t("playerIntel.loadingList") : t("playerIntel.noPlayers")}</Empty>
            ) : (
              players.value.map((p) => (
                <button
                  key={p.pid}
                  class={`intel-player-row ${selectedPid.value === p.pid ? "active" : ""}`}
                  onClick={() => void select(p.pid)}
                >
                  <span class="intel-player-id">
                    <b class="intel-player-name">
                      {clanTagPill(p.clanTag) && <span class="pi-clan-pill mono">{clanTagPill(p.clanTag)}</span>}
                      <span class="ipn-text">{p.name}</span>
                    </b>
                    <span class="muted mono">PID {p.pid}</span>
                  </span>
                  <span class="intel-player-meta">
                    <span class="ipm-server" title={serverLabel(p)}>{serverLabel(p)}</span>
                    <span>{fmtTs(p.lastSeenAt)}</span>
                    <span>{p.seenCount} {t("playerIntel.seenLower")}</span>
                    {canViewGeo && <span>{p.geoCountryName || p.geoCountryCode || "—"}</span>}
                  </span>
                </button>
              ))
            )}
          </div>
        </Panel>

        <PlayerDetailPanel player={detail.value} canViewIp={canViewIp} canViewGeo={canViewGeo} onRefreshGeo={() => void refreshGeo()} geoLooking={geoLooking.value} />
      </div>
    </div>
  );
}
